import { BUTTON_LABELS } from '@/constants';
import { ButtonProps, RoleplayCardProps } from '@/components';
import { ISimulation, IPersona, ISimulationScore } from '@/types';

/**
 * Maps a domain-level `ISimulation` into `RoleplayCardProps` for UI rendering.
 *
 * This utility consolidates simulation metadata with related persona/avatar details,
 * derives action button configuration, computes whether the simulation should be
 * considered "new", and attaches score history scoped to this simulation.
 *
 * Behavior summary:
 * - Action label:
 *   - `Try again` when simulation is completed OR has non-empty history.
 *   - `Get started` otherwise.
 * - Action disabled state:
 *   - Disabled only when simulation status is `locked`.
 * - New badge (`isNew`):
 *   - `true` by default.
 *   - switches to `false` only when there are 2 or more history attempts.
 * - History in returned props:
 *   - Includes only scores whose `simulationId` matches `simulation.id`.
 */
export function mapSimulationToRoleplayCard({
  simulation,
  personas,
  scores,
  onClick,
}: {
  simulation: ISimulation;
  personas: IPersona[];
  scores: ISimulationScore[];
  onClick: (event?: React.MouseEvent<HTMLButtonElement>) => void;
}): RoleplayCardProps {
  // Treat simulations as "new" until the attempt history says otherwise.
  let isNew = true;

  // `RoleplayCard` accepts one or more actions; this mapper currently emits one
  // primary action for start/retry behavior.
  const actions: ButtonProps[] = [];

  // Resolve the avatar/persona associated with this simulation.
  const avatar = personas.find((p) => p.id === simulation.personaId);

  /**
   * Determines the action button label shown on the card.
   *
   * Notes:
   * - Completed simulations should always prompt retry behavior.
   * - Any non-zero history length also prompts retry behavior.
   * - Otherwise, first-time users get a start label.
   */
  const getActionLabel = () => {
    if (simulation.status === 'completed' || simulation?.history?.length !== 0) {
      return BUTTON_LABELS.TRY_AGAIN;
    }

    return BUTTON_LABELS.GET_STARTED;
  };

  // Create the single card action with shared shape used by `ButtonProps`.
  actions.push({
    label: getActionLabel(),
    onClick,
    type: 'button',
    variant: 'secondary',
    // Locked simulations are not actionable from this card.
    disabled: simulation.status === 'locked',
  });

  if (simulation?.history?.length) {
    /**
     * If the user has a history with this simulation, we check if they
     * have tried it at least twice.
     *
     *  - If they have tried it at least twice, we consider it not new
     *    and show the "Try Again" label on the button.
     *  - If they have only tried it once, we still consider it new and
     *    show the "Get Started" label on the button.
     */
    const hasTriedTwoOrMoreTimes = simulation.history.length >= 2;

    // A simulation is no longer considered "new" once it has at least two attempts.
    if (hasTriedTwoOrMoreTimes) isNew = false;
  }

  /**
   * Construct the final `RoleplayCardProps` payload.
   *
   * Key mappings:
   * - Base simulation fields are spread directly.
   * - Persona-derived avatar fields are optional when no persona is found.
   * - `history` is normalized to scores belonging to this simulation ID.
   */
  const roleplayCardData: RoleplayCardProps = {
    ...simulation,
    actions,
    isNew,
    avatarName: avatar?.name,
    avatarSrc: avatar?.imageUrl,
    avatarAlt: avatar ? `${avatar.name} avatar` : undefined,
    history: scores.filter((score) => score.simulationId === simulation.id.toString()),
  };

  return roleplayCardData;
}
